import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowUpRight } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { usePaywallStore } from "@/stores/use-paywall-store";
import type { Metric } from "@/services/billing";

const METRIC_LABELS: Record<Metric, string> = {
  ai_conversations: "Conversas IA",
  ai_messages: "Mensagens IA",
};

function formatInt(value: number): string {
  return value.toLocaleString("pt-BR");
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function UsageLimitModal() {
  const navigate = useNavigate();
  const paywall = usePaywallStore((s) => s.paywall);
  const close = usePaywallStore((s) => s.close);

  const metricLabel = paywall.metric ? METRIC_LABELS[paywall.metric] : "uso";
  const resetDate = formatDate(paywall.resetAt);

  const handleBuyCredits = () => {
    close();
    if (paywall.metric) {
      navigate(`/settings/usage?buy=${paywall.metric}`);
    } else {
      navigate("/settings/usage");
    }
  };

  // Growth is the single plan: there is no upgrade, so the CTA opens the
  // single-plan notice, which points the clinic to the CliniSharp team.
  const handleContact = () => {
    close();
    navigate("/settings/billing/upgrade");
  };

  return (
    <Dialog
      open={paywall.open}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent
        data-testid="usage-limit-modal"
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>Limite de {metricLabel} atingido</DialogTitle>
          <DialogDescription>
            Você usou{" "}
            <span className="font-semibold text-foreground">
              {formatInt(paywall.planUsed)}
            </span>{" "}
            de{" "}
            <span className="font-semibold text-foreground">
              {formatInt(paywall.planLimit)}
            </span>
            {resetDate ? (
              <>
                . Seu reset acontece em{" "}
                <span className="font-medium text-foreground">{resetDate}</span>
                .
              </>
            ) : (
              "."
            )}
          </DialogDescription>
        </DialogHeader>

        {paywall.creditsAvailable > 0 && (
          <p className="rounded-md bg-teal-50 px-3 py-2 text-xs text-teal-800">
            Você ainda tem{" "}
            <span className="font-semibold">
              {formatInt(paywall.creditsAvailable)}
            </span>{" "}
            créditos extras disponíveis.
          </p>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleBuyCredits}
          >
            <Sparkles className="mr-2 h-4 w-4" />
            Comprar créditos
          </Button>
          <Button
            type="button"
            className="w-full"
            onClick={handleContact}
          >
            <ArrowUpRight className="mr-2 h-4 w-4" />
            Falar com a CliniSharp
          </Button>
        </div>

        <button
          type="button"
          className="mx-auto text-xs text-muted-foreground underline-offset-2 hover:underline"
          onClick={close}
        >
          Aguardar reset
        </button>
      </DialogContent>
    </Dialog>
  );
}
